import { TouchEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTrackEvent } from '@hooks/useTrackEvent';
import i18n from '@i18n/index';
import { ComponentVisibility } from '@models/checkIn';

const TAP_MOVEMENT_THRESHOLD_PX = 10;

interface Props {
  value: ComponentVisibility;
  onChange: (visibility: ComponentVisibility) => void;
  /**
   * Optional analytics tag for distinguishing different visibility-toggle
   * locations in Firebase (e.g. 'check_in_battery', 'photo_caption',
   * 'note_compose'). Without this, all toggles look the same in the
   * dashboard. Backend only sees the FINAL persisted value, so this event
   * captures pre-save fiddle (toggling between options before settling).
   */
  trackingId?: string;
}

interface VisibilityOption {
  value: ComponentVisibility;
  i18nKey: string;
  fontSize: number;
  allowWrap?: boolean;
}

// Per-option font sizing tuned for 320px viewport with equal-width chips.
// "Public" and "Friends" fit at the original 14px. "Only Me" needs 12px to fit
// on one line. "Close Friends" wraps to two lines at 11px so the row stays
// compact instead of overflowing or growing chip height too much.
export const VISIBILITY_OPTIONS: VisibilityOption[] = [
  { value: ComponentVisibility.PUBLIC, i18nKey: 'visibility.public', fontSize: 14 },
  { value: ComponentVisibility.FRIENDS, i18nKey: 'visibility.friends', fontSize: 14 },
  {
    value: ComponentVisibility.CLOSE_FRIENDS,
    i18nKey: 'visibility.close_friends',
    fontSize: 11,
    allowWrap: true,
  },
  { value: ComponentVisibility.ONLY_ME, i18nKey: 'visibility.only_me', fontSize: 12 },
];

export function getVisibilityLabel(value: ComponentVisibility): string {
  const opt = VISIBILITY_OPTIONS.find((o) => o.value === value);
  return opt ? i18n.t(opt.i18nKey) : '';
}

function VisibilityToggle({ value, onChange, trackingId }: Props) {
  const [t] = useTranslation('translation');
  const trackEvent = useTrackEvent();
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const handleChange = (next: ComponentVisibility) => {
    if (next === value) return;
    trackEvent('visibility_toggle_changed', {
      from: String(value),
      to: String(next),
      ...(trackingId ? { surface: trackingId } : {}),
    });
    onChange(next);
  };

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
  };

  // Fire on touchend (with movement guard) so the action isn't lost when
  // a software keyboard is up — iOS WebView can swallow the synthesized
  // click while it dismisses the keyboard / reflows the viewport.
  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>, next: ComponentVisibility) => {
    const start = touchStartPosRef.current;
    touchStartPosRef.current = null;
    if (!start) return;
    const touch = e.changedTouches[0];
    const dx = Math.abs(touch.clientX - start.x);
    const dy = Math.abs(touch.clientY - start.y);
    if (dx >= TAP_MOVEMENT_THRESHOLD_PX || dy >= TAP_MOVEMENT_THRESHOLD_PX) return;
    e.preventDefault();
    e.stopPropagation();
    handleChange(next);
  };

  return (
    <ToggleContainer>
      {VISIBILITY_OPTIONS.map((opt) => (
        <ToggleOption
          key={opt.value}
          $isSelected={value === opt.value}
          onTouchStart={handleTouchStart}
          onTouchEnd={(e) => handleTouchEnd(e, opt.value)}
          onClick={() => handleChange(opt.value)}
        >
          <Label
            $isSelected={value === opt.value}
            $fontSize={opt.fontSize}
            $allowWrap={opt.allowWrap ?? false}
          >
            {t(opt.i18nKey)}
          </Label>
        </ToggleOption>
      ))}
    </ToggleContainer>
  );
}

const ToggleContainer = styled.div`
  display: flex;
  gap: 2px;
  background-color: ${({ theme }) => theme.BACKGROUND_COLOR};
  border-radius: 8px;
  padding: 2px;
  width: 100%;
  align-items: stretch;
`;

const ToggleOption = styled.div<{ $isSelected: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 4px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ $isSelected, theme }) => ($isSelected ? theme.WHITE : 'transparent')};
  border: 1px solid ${({ $isSelected, theme }) => ($isSelected ? theme.PRIMARY : 'transparent')};
  transition: all 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
`;

const Label = styled.span<{ $isSelected: boolean; $fontSize: number; $allowWrap: boolean }>`
  font-size: ${({ $fontSize }) => $fontSize}px;
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 400)};
  color: ${({ $isSelected, theme }) => ($isSelected ? theme.PRIMARY : theme.MEDIUM_GRAY)};
  white-space: ${({ $allowWrap }) => ($allowWrap ? 'normal' : 'nowrap')};
  text-align: center;
  line-height: 1.2;
`;

export default VisibilityToggle;
